import type { ItemId } from './inventory';
import type { DeployManager, Claim } from './deployables';
import type { Builder } from './building';
import { UPKEEP, BUILD } from '../config';

// BASE-CLAIM UPKEEP + DECAY. Each in-game day, every claim is charged a share (UPKEEP.ratePerDay) of its base's total
// build materials, taken from the claim chest (its `contents`). If the chest can't cover the bill the base enters
// DECAY: after a grace period, one piece crumbles every UPKEEP.decayIntervalSeconds -- weakest tier first -- until
// upkeep is paid again (a later day with a stocked chest) or the base is gone. The owner picked Rust-style decay at
// 20%/day. While a claim IS stocked (this instant's bill is payable), its pieces self-heal toward full HP
// (UPKEEP.selfHealHpPerSec) -- a maintained base quietly repairs itself. Pieces with no claim covering them at all
// always lose HP (UPKEEP.unclaimedDecay, twig fastest) so an abandoned free-build rots away with no chest to save it.
// Authority-side logic lives here; main ticks it off the day-night clock and reads status() for the chest UI.

interface UpState { paid: boolean; grace: number; decayT: number; warned: boolean; acc: Map<ItemId, number> }

export interface UpkeepStatus {
  cost: Array<{ item: ItemId; amount: number }>; // today's bill, by item
  daysStocked: number;                            // whole days the chest can cover (-1 = nothing built / no bill)
  decaying: boolean;                              // unpaid AND past the grace period (actively withering)
  graceLeft: number;                              // seconds of grace remaining before decay starts (-1 = paid up)
  tier: 'green' | 'yellow' | 'red';               // claim status lamp: stocked / low-or-grace / decaying
}

export class UpkeepManager {
  // WeakMap (not Map): we never iterate this -- tick() walks deployables.eachClaim() and looks state up per claim -- so
  // keying by the Claim object lets a destroyed claim's state be garbage-collected automatically (no manual cleanup).
  private readonly state = new WeakMap<Claim, UpState>();
  onWarn: ((msg: string) => void) | null = null; // fired once when a base starts decaying (grace just expired)
  onDecay: ((x: number, z: number) => void) | null = null; // fired every time a piece actually crumbles (main plays the crumble SFX there)

  constructor(private readonly deployables: DeployManager, private readonly builder: Builder) {}

  // Today's upkeep bill for a claim: ratePerDay of the base's total build materials (rounded up), per item.
  dailyCost(claim: Claim): Array<{ item: ItemId; amount: number }> {
    const mats = this.builder.baseMaterials(claim.x, claim.z, BUILD.cupboardRadius);
    const out: Array<{ item: ItemId; amount: number }> = [];
    for (const [item, total] of mats) { const a = Math.ceil(total * UPKEEP.ratePerDay); if (a > 0) out.push({ item, amount: a }); }
    return out;
  }

  private st(claim: Claim): UpState {
    let s = this.state.get(claim);
    if (!s) { s = { paid: true, grace: UPKEEP.graceSeconds, decayT: 0, warned: false, acc: new Map() }; this.state.set(claim, s); }
    return s;
  }

  // Crumble one piece in the claim (one decay step). Returns true if a piece was removed.
  decayStep(claim: Claim): boolean {
    return this.builder.decayOneIn(claim.x, claim.z, BUILD.cupboardRadius);
  }

  // Per-frame driver: CONTINUOUSLY drain each claim chest (UPKEEP.drainPerMinute of the base's total build cost per
  // real minute, per item, rounded DOWN via a fractional accumulator -- so a modest base visibly loses ~1 wood/min),
  // self-heal pieces while the chest can still pay, and once ANY needed material runs dry give a grace window then
  // crumble one piece every decayInterval (decayOneIn crumbles the OUTER/weakest pieces first, inward).
  tick(dt: number, _totalDays: number): void {
    for (const c of this.deployables.eachClaim()) {
      const s = this.st(c);
      const mats = this.builder.baseMaterials(c.x, c.z, BUILD.cupboardRadius);
      let anyOwed = false, starved = false;
      for (const [item, total] of mats) {
        if (total <= 0) continue;
        anyOwed = true;
        let acc = (s.acc.get(item) ?? 0) + total * UPKEEP.drainPerMinute * (dt / 60); // fractional units owed this frame
        const want = Math.floor(acc);
        if (want >= 1) { const took = c.contents.take(item, want); acc -= took; if (took < want) starved = true; } // round DOWN; take what's there
        if (c.contents.count(item) <= 0) { starved = true; if (acc > 1) acc = 0.99; } // this material is dry -> starving; don't hoard unpayable debt
        s.acc.set(item, acc);
      }
      // Decay disabled (UPKEEP.witherHpPerSec = 0, owner 2026-07-08) => never mark a base unpaid: no red lamp,
      // no "decaying" warning, no wither. The knob re-arms the whole path live if decay comes back.
      const sustained = !anyOwed || !starved || UPKEEP.witherHpPerSec <= 0; // an empty base owes nothing; otherwise sustained while the chest still covers every material
      if (sustained) {
        if (!s.paid) { s.paid = true; s.grace = UPKEEP.graceSeconds; s.decayT = 0; s.warned = false; } // restocked: reset the decay clock + grace
        this.builder.healIn(c.x, c.z, BUILD.cupboardRadius, UPKEEP.selfHealHpPerSec, dt);
      } else {
        s.paid = false;
        if (s.grace > 0) { s.grace = Math.max(0, s.grace - dt); if (s.grace <= 0 && !s.warned) { s.warned = true; this.onWarn?.('Base decaying — add upkeep!'); } }
        // Continuous WITHER (owner spec): a few outermost pieces drain HP visibly (hammer shows the bars
        // dropping -- the tell that the claim ran dry) instead of one piece silently popping per interval.
        else if (this.builder.witherIn(c.x, c.z, BUILD.cupboardRadius, UPKEEP.witherHpPerSec, dt, UPKEEP.witherPieces)) this.onDecay?.(c.x, c.z);
      }
    }
    // Pieces with NO claim covering them at all (built outside every cupboard radius) decay by HP, tier-rated.
    // Skipped entirely while every rate is 0 (decay off) -- no per-frame walk of the placed map for nothing.
    if (Object.values(UPKEEP.unclaimedDecay).some((v) => v > 0)) {
      this.builder.decayUnclaimed(UPKEEP.unclaimedDecay, dt, (x, z) => !!this.deployables.claimAt(x, z, BUILD.cupboardRadius));
    }
  }

  // For the claim-chest UI: the bill, how many whole days the chest covers, and whether it's actively decaying.
  status(claim: Claim): UpkeepStatus {
    const cost = this.dailyCost(claim);
    const s = this.st(claim);
    let days = Infinity;
    for (const c of cost) days = Math.min(days, Math.floor(claim.contents.count(c.item) / c.amount));
    const decaying = !s.paid && s.grace <= 0;
    const graceLeft = s.paid ? -1 : Math.ceil(s.grace);
    // Owner lamp semantics: RED = decaying OR nothing stocked at all; YELLOW = grace period / can't cover
    // a full day; GREEN = stocked and good. An empty chest is never green, even with no bill yet.
    const tier: UpkeepStatus['tier'] = (decaying || claim.contents.isEmpty()) ? 'red' : (!s.paid || (cost.length > 0 && days < 1)) ? 'yellow' : 'green';
    return { cost, daysStocked: cost.length ? days : -1, decaying, graceLeft, tier };
  }
}
